/**
 * Preferences › Updates: on Preview, the version to run, shown with the
 * hidden channel switch. "Latest" follows the newest Preview; any listed
 * version (none older than Production's) installs that exact build, restarts
 * and stays on it until Latest is picked again. A pick confirms first.
 */
import { useEffect, useState } from "react";
import { ConfirmDialog, PocketRow, SelectField } from "@/components/ui";
import {
  installPreviewVersion,
  loadPreviewVersions,
  type PreviewVersions,
} from "@/desktop/preview-version";
import { useAppVersion } from "@/desktop/use-app-version";
import { useT } from "@/i18n";
import { releaseChannelMessages } from "@/i18n/messages/release-channel";
import { releaseChannel } from "@/lib/release-channel";

const LATEST = "latest";

export function PreviewVersionRow() {
  return releaseChannel() === "preview" ? <PreviewVersionPicker /> : null;
}

function PreviewVersionPicker() {
  const t = useT(releaseChannelMessages);
  const running = useAppVersion();
  const [list, setList] = useState<PreviewVersions | null>(null);
  const [failed, setFailed] = useState(false);
  const [target, setTarget] = useState<string | null>(null);
  const [installing, setInstalling] = useState(false);

  useEffect(() => {
    if (target !== null) return;
    let cancelled = false;
    loadPreviewVersions()
      .then((loaded) => {
        if (cancelled) return;
        setList(loaded);
        setFailed(false);
      })
      .catch(() => {
        if (!cancelled) setFailed(true);
      });
    return () => {
      cancelled = true;
    };
  }, [target]);

  const value = list?.pinned ?? LATEST;
  const versions = list?.versions ?? [];
  const confirm = (picked: string) => {
    setInstalling(true);
    void installPreviewVersion(picked === LATEST ? null : picked).finally(
      () => {
        setInstalling(false);
        setTarget(null);
      },
    );
  };

  return (
    <>
      <PocketRow
        testId="settings-row-preview-version"
        title={t("previewVersion")}
        secondLine={failed ? t("versionsFailed") : undefined}
        trailing={
          <SelectField
            variant="value"
            value={value}
            valueLabel={value === LATEST ? t("latest") : value}
            options={[
              {
                value: LATEST,
                label: t("latest"),
                trailing: versions[0]?.version,
              },
              ...versions.map(({ version, title }) => ({
                value: version,
                label: version,
                description: title,
                trailing: version === running ? t("thisVersion") : undefined,
              })),
            ]}
            onChange={(next) => {
              if (next !== value) setTarget(next);
            }}
            disabled={!list}
            testId="preview-version-select"
            ariaLabel={t("previewVersion")}
          />
        }
      />
      {target && (
        <ConfirmDialog
          title={
            target === LATEST
              ? t("latestTitle")
              : t("installTitle", { version: target })
          }
          description={target === LATEST ? t("latestBody") : t("installBody")}
          confirmLabel={
            target === LATEST ? t("latestAction") : t("installAction")
          }
          variant="default"
          busy={installing}
          onConfirm={() => confirm(target)}
          onCancel={() => {
            if (!installing) setTarget(null);
          }}
          data-testid="preview-version-confirm"
        />
      )}
    </>
  );
}
